import React, { useMemo, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Typography, Button, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Greetings, RecentTransactions, Shortcuts, LoanEligibilityCard } from "@/components/common";
import { useTheme } from "@/components/common/ThemeProvider";
import ImportantAttachments from "@/views/posts/ImportantDownloads";
import { useAllAccountBalances, useBalanceByProduct } from "@/hooks/accounts/useCustomerAccount";
import { useAuthUser } from "@/modules/auth/context/AuthContext";
// import { PendingGuarantorRequests } from "@/modules/loans"; // TODO: Component not implemented yet
import { LoanCalculatorCard } from "@/components/loan-calculator";
import { WalletOutlined, BankOutlined, EyeOutlined, EyeInvisibleOutlined, FileProtectOutlined } from '@ant-design/icons';
import { format } from "@/components/common/utils/money";
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useDeductionStatus } from "@/hooks/admin/profile";
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface DashboardBalanceRow {
    key: string;
    productName: string;
    glNo: string;
    amount: number;
    sessionDate?: string;
}

const Home: React.FC = () => {
    const { dark } = useTheme();
    const authUser = useAuthUser();
    const navigate = useNavigate();
    const { branch, custNo } = authUser;
    const [isBalanceVisible, setIsBalanceVisible] = useState<boolean>(false);
    const { data: deductionStatus } = useDeductionStatus();
    const showDeductionBanner = !!(
        deductionStatus?.deductionFormRequired &&
        !deductionStatus?.signedDeductionFormUploaded
    );

    const [savingsQuery] = useBalanceByProduct(branch || '', custNo || '', 'S');
    const [sharesQuery] = useBalanceByProduct(branch || '', custNo || '', 'H');
    const [loanQuery] = useBalanceByProduct(branch || '', custNo || '', 'L');
    const [allBalancesQuery] = useAllAccountBalances(branch || '', custNo || '');
    const collapsedPanelMinHeight = 170;

    const canBeGuarantor = authUser?.primaryGroup === 'MEMBER';

    const savingsBalance = (savingsQuery.data?.balance || 0) * -1;
    const sharesBalance = (sharesQuery.data?.balance || 0) * -1;
    const loanOutstanding = Math.abs(loanQuery.data?.balance || 0);
    const balanceRows: DashboardBalanceRow[] = (allBalancesQuery.data || []).map((item, index) => ({
        key: `${item.glNo || 'gl'}-${index}`,
        productName: item.productName || item.ledgerName || '-',
        glNo: item.glNo || '-',
        amount: item.amount || 0,
        sessionDate: item.sessionDate,
    }));
    const netPosition = savingsBalance + sharesBalance - loanOutstanding;
    const netWorthChartData = useMemo(
        () => [
            { name: 'Savings', value: savingsBalance, color: '#2563eb' },
            { name: 'Shares', value: sharesBalance, color: '#0f766e' },
            { name: 'Loans', value: -loanOutstanding, color: '#dc2626' },
            {
                name: 'Net Position',
                value: netPosition,
                color: netPosition >= 0 ? '#16a34a' : '#f59e0b',
            },
        ],
        [loanOutstanding, netPosition, savingsBalance, sharesBalance]
    );
    const asOfDate = balanceRows[0]?.sessionDate
        ? new Date(balanceRows[0].sessionDate).toLocaleDateString()
        : '-';

    const balanceColumns: ColumnsType<DashboardBalanceRow> = [
        {
            title: 'Product',
            dataIndex: 'productName',
            key: 'productName',
        },
        {
            title: 'GL No',
            dataIndex: 'glNo',
            key: 'glNo',
        },
        {
            title: 'Balance',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (value: number) => <Text strong>{format(value)}</Text>,
        },
    ];

    return (
        <div className={dark ? 'bg-gray-900' : 'bg-gray-50'}>
            {showDeductionBanner && (
                <Alert
                    icon={<FileProtectOutlined />}
                    message="Action Required: Signed Deduction Form Pending"
                    description={
                        <span>
                            An official deduction form has been prepared for your account.
                            Please <strong>download it, get it signed, and upload the signed copy</strong> via your profile page.{' '}
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: 0 }}
                                onClick={() => navigate('/user/profile')}
                            >
                                Go to Profile →
                            </Button>
                        </span>
                    }
                    type="warning"
                    showIcon
                    banner
                    style={{ marginBottom: 16 }}
                />
            )}
            <Greetings />
                
                {/* Enhanced Balance Cards with Icons and Gradients */}
                <Row gutter={[24, 24]} className="mb-8">
                    <Col xs={24} sm={12} lg={8}>
                        <Card
                            className={`shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 overflow-hidden ${
                                dark ? 'bg-gradient-to-br from-blue-900 to-blue-800' : 'bg-gradient-to-br from-blue-500 to-[#0084d1]'
                            }`}
                            bodyStyle={{ padding: '24px' }}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center mb-2">
                                        <WalletOutlined className="text-2xl text-white opacity-80 mr-2" />
                                        <span className="text-white text-sm font-medium opacity-90">Savings Balance</span>
                                    </div>
                                    <Statistic
                                        value={savingsQuery.isLoading ? 0 : savingsBalance}
                                        precision={2}
                                        valueStyle={{ color: '#fff', fontSize: '24px', fontWeight: 'bold' }}
                                        prefix="TSH"
                                        loading={savingsQuery.isLoading}
                                    />
                                </div>
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                    dark ? 'bg-blue-800 bg-opacity-50' : 'bg-white bg-opacity-20'
                                }`}>
                                    <WalletOutlined className="text-2xl text-white" />
                                </div>
                            </div>
                        </Card>
                    </Col>
                    
                    <Col xs={24} sm={12} lg={8}>
                        <Card
                            className={`shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 overflow-hidden ${
                                dark ? 'bg-gradient-to-br from-red-900 to-red-800' : 'bg-gradient-to-br from-[#0084d1] to-green-600'
                            }`}
                            bodyStyle={{ padding: '24px' }}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center mb-2">
                                        <BankOutlined className="text-2xl text-white opacity-80 mr-2" />
                                        <span className="text-white text-sm font-medium opacity-90">Shares Balance</span>
                                    </div>
                                    <Statistic
                                        value={sharesQuery.isLoading ? 0 : sharesBalance}
                                        precision={2}
                                        valueStyle={{ color: '#fff', fontSize: '24px', fontWeight: 'bold' }}
                                        prefix="TSH"
                                        loading={sharesQuery.isLoading}
                                    />
                                </div>
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                    dark ? 'bg-purple-800 bg-opacity-50' : 'bg-white bg-opacity-20'
                                }`}>
                                    <BankOutlined className="text-2xl text-white" />
                                </div>
                            </div>
                        </Card>
                    </Col>
                    
                    <Col xs={24} sm={24} lg={8}>
                        <LoanEligibilityCard />
                    </Col>
                </Row>

                {/* Loan Calculator Section */}
                <Row gutter={[24, 24]} className="mb-8">
                    <Col xs={24} lg={12} className="flex">
                        <LoanCalculatorCard className="w-full" collapsedPlaceholderMinHeight={collapsedPanelMinHeight} />
                    </Col>
                    <Col xs={24} lg={12} className="flex">
                        <Card
                            className={`shadow-lg border-0 h-full w-full ${dark ? 'bg-gray-800' : 'bg-white'}`}
                            bodyStyle={{ padding: '16px' }}
                            title={
                                <div className="flex items-center justify-between gap-3">
                                    <span className={dark ? 'text-gray-100' : 'text-gray-800'}>My Balances (As of {asOfDate})</span>
                                    <Button
                                        type={isBalanceVisible ? 'default' : 'primary'}
                                        onClick={() => setIsBalanceVisible(!isBalanceVisible)}
                                        icon={isBalanceVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                        size="small"
                                    >
                                        {isBalanceVisible ? 'Hide Balances' : 'Show Balances'}
                                    </Button>
                                </div>
                            }
                        >
                            {!isBalanceVisible ? (
                                <div style={{ minHeight: collapsedPanelMinHeight }} className="flex items-center">
                                    <Alert
                                        className="w-full"
                                        type="info"
                                        showIcon
                                        message="Balances are hidden"
                                        description="Click 'Show Balances' to reveal your account balances."
                                    />
                                </div>
                            ) : (
                                <Table
                                    rowKey="key"
                                    columns={balanceColumns}
                                    dataSource={balanceRows}
                                    loading={allBalancesQuery.isLoading}
                                    pagination={false}
                                    size="small"
                                    locale={{ emptyText: 'No balances found' }}
                                    scroll={{ x: 420 }}
                                />
                            )}
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[24, 24]} className="mb-8">
                    <Col xs={24}>
                        <Card
                            className={`shadow-lg border-0 ${dark ? 'bg-gray-800' : 'bg-white'}`}
                            bodyStyle={{ padding: '16px' }}
                            title={<span className={dark ? 'text-gray-100' : 'text-gray-800'}>Financial Position Snapshot</span>}
                            extra={<Text type="secondary">Assets vs Liabilities</Text>}
                        >
                            <>
                                <div style={{ width: '100%', height: 280 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={netWorthChartData} margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#374151' : '#e5e7eb'} />
                                            <XAxis dataKey="name" stroke={dark ? '#d1d5db' : '#4b5563'} />
                                            <YAxis
                                                stroke={dark ? '#d1d5db' : '#4b5563'}
                                                tickFormatter={(value: number) => format(value)}
                                                width={90}
                                            />
                                            <Tooltip
                                                formatter={(value: number) => format(value)}
                                                contentStyle={{
                                                    backgroundColor: dark ? '#1f2937' : '#ffffff',
                                                    borderColor: dark ? '#374151' : '#e5e7eb',
                                                }}
                                            />
                                            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                                {netWorthChartData.map((entry) => (
                                                    <Cell key={entry.name} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <Text type={netPosition >= 0 ? 'success' : 'warning'}>
                                    Net Position: {format(netPosition)}
                                </Text>
                            </>
                        </Card>
                    </Col>
                </Row>

                {/* Main Content Area */}
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={16}>
                        <Card 
                            className={`shadow-lg border-0 ${dark ? 'bg-gray-800' : 'bg-white'}`}
                            bodyStyle={{ padding: 0 }}
                        >
                            <RecentTransactions authUser={authUser} dark={dark} />
                        </Card>
                    </Col>
                    
                    <Col xs={24} lg={8}>
                        <div className="space-y-6">
                            <Card 
                                className={`shadow-lg border-0 ${dark ? 'bg-gray-800' : 'bg-white'}`}
                                bodyStyle={{ padding: '20px' }}
                            >
                                <Shortcuts dark={dark} />
                            </Card>
                            
                            {/* TODO: Component not implemented yet */}
                            {/* {canBeGuarantor && (
                                <div className="transform transition-all duration-300 hover:scale-[1.02]">
                                    <PendingGuarantorRequests />
                                </div>
                            )} */}
                            
                            <div className="transform transition-all duration-300 hover:scale-[1.02]">
                                <ImportantAttachments />
                            </div>
                        </div>
                    </Col>
                </Row>
        </div>
    );
}

export default Home;